/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import { Box, Flex, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';

// UI local components
import {
	Button,
	Text,
	TextInput,
} from '@components/v2/ui';
import EmailChangeVerificationModal from '../EmailChangeVerificationModal/EmailChangeVerificationModal';
import ProfileSectionCard from '../ProfileSectionCard/ProfileSectionCard';

// Styles
import classes from './ProfileChangeEmail.module.css';

// Apis
import { useWhoAmI } from '@api/get-whoami';

// Constants
import { REGEX_EMAIL } from '@config/constants/regex';
import { useUpdateEmail } from '@features/account-settings/api/update-email';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                    Type                                     */
/* -------------------------------------------------------------------------- */
type TFormValuesChangeEmail = {
	email: string;
	confirmEmail: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const ProfileChangeEmail = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const [opened, handlers] = useDisclosure(false);

	const updateEmail = useUpdateEmail({
		mutationConfig: {
			onSuccess() {
				handlers.toggle();
				formChangeEmail.reset();
			},
		},
	});

	const { data: user } = useWhoAmI();

	const formChangeEmail = useForm<TFormValuesChangeEmail>(
		{
			initialValues: {
				email: '',
				confirmEmail: '',
			},
			validate: {
				email: (value) => {
					if (!REGEX_EMAIL.test(value))
						return t('form.error.invalidEmail');
					if (value == user?.email)
						return t('global.forbiddenEmailAddress');
					return null;
				},

				confirmEmail: (value, values) => {
					if (value !== values.email)
						return t('global.emailsDidNotMatch');
					if (value == user?.email)
						return t('global.forbiddenEmailAddress');
					return null;
				},
			},
		}
	);

	/* ******************************** RENDERING ******************************* */
	return (
		<ProfileSectionCard
			title={t('global.emailAddressChange')}
		>
			<EmailChangeVerificationModal
				opened={opened}
				onClose={handlers.close}
				expiresInMinutes={30}
			/>

			<form
				onSubmit={formChangeEmail.onSubmit((values) => {
					updateEmail.mutate({
						data: { email: values.email, id: user?.id },
					});
				})}
			>
				<Flex
					align={'center'}
					gap={'32px'}
					mb={'32px'}
					flex={1}
					wrap={'wrap'}
				>
					<Box className={classes.bloc}>
						<Stack gap={'8px'}>
							<Text variant="caption" c={'#9BA6B7'}>
								Adresse mail actuelle
							</Text>
							<Text variant="subtitle1" c={'#505058'}>
								{user?.email}
							</Text>
						</Stack>
					</Box>
					<Box className={classes.bloc}>
						<TextInput
							key={formChangeEmail.key('email')}
							withAsterisk
							label={t('global.newAdresseMail')}
							placeholder={'example@email.com'}
							{...formChangeEmail.getInputProps('email')}
						/>
					</Box>
					<Box className={classes.bloc}>
						<TextInput
							key={formChangeEmail.key('confirmEmail')}
							withAsterisk
							label={t('global.confirmeAdresseMail')}
							placeholder={'example@email.com'}
							{...formChangeEmail.getInputProps(
								'confirmEmail'
							)}
						/>
					</Box>
				</Flex>
				<Flex justify={'end'}>
					<Button
						loading={updateEmail?.isPending}
						disabled={!formChangeEmail.isDirty()}
						type="submit"
						variant="primary"
						size={'md'}
					>
						{t('global.validateEmailAddressChange')}
					</Button>
				</Flex>
			</form>
		</ProfileSectionCard>
	);
};

export default ProfileChangeEmail;
